import styled from "styled-components";
import { designTokens, getTypography } from "./designTokens";

interface FlexContainerProps {
  direction?: string;
  align?: string;
  justify?: string;
  gap?: string;
}

interface ButtonProps {
  variant?: "outlined" | "contained";
}

interface CardProps {
  background?: string;
  borderColor?: string;
}

// Layout
export const FlexContainer = styled.div<FlexContainerProps>`
  display: flex;
  flex-direction: ${(props) => props.direction || "row"};
  align-items: ${(props) => props.align || "center"};
  justify-content: ${(props) => props.justify || "center"};
  gap: ${(props) => props.gap || "0"};
`;

export const PageContainer = styled.div`
  width: 100%;
  padding: 120px 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Layout Components
export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #ffffff;
`;

export const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 40px 0;
  background-color: #ffffff;
`;

export const EmptySpace = styled.div`
  height: 100px;
`;
export const KoHeadline = styled.div`
  ${getTypography("korean", "headline1")}
  color: ${designTokens.colors.font.primary};
`;

export const KoTitle1 = styled.div`
  ${getTypography("korean", "title1")}
  font-weight: 600;
  padding: 0px 10px;
  color: ${designTokens.colors.font.primary};

`;

export const EnHeadline = styled.div`
  ${getTypography("english", "headline1")}
  color: ${designTokens.colors.font.primary};
`;

export const EnTitle2 = styled.div`
  ${getTypography("english", "title2")}
  padding: 0px 10px;
`;

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 8px 24px rgba(12, 39, 58, 0.06);
  overflow-x: auto;
  border: 1px solid rgba(92, 110, 255, 0.08);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const ResponsiveTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 480px) {
    display: block;
    thead { display: none; }
    tbody {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    tr {
      display: flex;
      flex-direction: column;
      padding: 16px;
      border: 1px solid #eef0f8;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }
    td {
      padding: 6px 0;
      border: none;
    }
  }
`;

export const TableHead = styled.thead`
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  transition: background 0.2s ease;
  &:hover td {
    background: rgba(92, 110, 255, 0.05);
  }
`;

export const TableHeader = styled.th`
  ${getTypography("korean", "body2")}
  color: ${designTokens.colors.font.primary};
  font-weight: 600;
  text-align: start;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(92, 110, 255, 0.1);
`;

export const TableCell = styled.td`
  ${getTypography("korean", "body3")}
  color: ${designTokens.colors.font.secondary};
  padding: 14px 16px;
  border-bottom: 1px solid #f2f3f8;
  vertical-align: middle;

  img {
    width: 104px;
    height: 100%;
    object-fit: cover;
  }

  img.icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
  }
`;

export const BaseActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;          /* 셀 너비 기준 중앙 */
  height: 100%;         /* 셀 높이 기준 중앙 */
  transition: transform 0.15s ease;
  &:hover {
    transform: scale(1.1);
  }
`;

export const UseButton = styled.button`
  ${getTypography("korean", "body4")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 15px 10px 20px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(90deg, #5c6eff 0%, #7385ff 100%);
  color: #ffffff;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(92, 110, 255, 0.25);
  transition: all 0.25s ease-in-out;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: linear-gradient(90deg, #4b5efc 0%, #6673ff 100%);
    box-shadow: 0 4px 8px rgba(92, 110, 255, 0.3);
    transform: translateY(-1px);

    svg {
      transform: translateX(3px);
    }
  }

  &:active {
    background: linear-gradient(90deg, #4b5efc 0%, #5c6eff 100%);
    box-shadow: 0 1px 3px rgba(92, 110, 255, 0.25);
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;


export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #b0b0b0;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: #e54848;
  }
`;



export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${designTokens.colors.font.quaternary};
  ${getTypography("korean", "body2")};
  span {
    color: #5c6eff;
    font-weight: 500;
  }
`;

export const EmptyIcon = styled.div`
  font-size: 42px;
  margin-bottom: 8px;
`;

// Title Components
export const Title = styled.h1`
  font-size: ${designTokens.typography.korean.headline1.fontSize};
  font-weight: ${designTokens.typography.fontWeight.regular};
  margin-bottom: 20px;
`;

// Container Components
export const VideoInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
`;

export const VideoTitle = styled.h2`
  font-size: ${designTokens.typography.korean.title1.fontSize};
  font-weight: ${designTokens.typography.fontWeight.regular};
  margin: 0;
`;

export const VideoInfoRow = styled.div`
  color: #5a5a5a;
  font-size: ${designTokens.typography.korean.body2.fontSize};
  font-weight: ${designTokens.typography.fontWeight.regular};
`;

// Typography
export const SmallTitle = styled.span`
  display: inline-flex;
  background-color: white;
  color: #30b0c7;
  padding: 9px 36px;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
`;

// Buttons
export const Button = styled.button<ButtonProps>`
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  border: 1px solid
    ${(props) => (props.variant === "outlined" ? "#30B0C7" : "transparent")};
  background-color: ${(props) =>
    props.variant === "outlined" ? "white" : "#30B0C7"};
  color: ${(props) => (props.variant === "outlined" ? "#30B0C7" : "white")};
`;

// Cards
export const Card = styled.div<CardProps>`
  padding: 24px 32px;
  border-radius: 12px;
  background: ${(props) => props.background || "white"};
  border: 1px solid ${(props) => props.borderColor || "#D1DBE8"};
`;

// Form Elements
export const Input = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0 12px;
`;

// Pagination Components
export const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ItemsPerPageContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  color: #5a5a5a;
`;

export const StyledSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #e5e8eb;
  border-radius: 4px;
  background: white;
  font-size: 14px;
`;

export const NavigationButton = styled.button<{ disabled: boolean }>`
  padding: 4px 8px;
  border: 1px solid #e5e8eb;
  border-radius: 4px;
  background: white;
  color: ${(props) => (props.disabled ? "#C4C4C4" : "#5A5A5A")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover:not(:disabled) {
    background: #f8f9fa;
  }
`;

// Result page shared styles
export const BoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-family: "DM Sans", serif;
  border-radius: 15px;
  padding: 50px;
  margin: 0 80px 50px 80px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-color: white;
  min-width: 1000px;
  max-width: 1100px;
  width: 100%;
  justify-content: center;
`;

export const ResultContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  font-family: "DM Sans", serif;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 50px;
`;

export const Container1 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  font-family: "DM Sans", serif;
  width: 100%;
`;

export const Container2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  font-family: "DM Sans", serif;
  width: 100%;
`;

export const Container3 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  font-family: "DM Sans", serif;
  width: 100%;
`;

export const ResultTitle = styled.div`
  font-size: 30px;
  color: #6d6d6d;
  font-family: "DM Sans", serif;
  font-weight: 700;
  margin-bottom: 20px;
`;

export const Items = styled.div`
  font-size: 25px;
  color: #21005d;
  font-family: "DM Sans", serif;
  font-weight: 700;
  text-decoration: underline;
`;

export const Result = styled.div`
  font-size: 25px;
  color: #21005d;
  font-family: "DM Sans", serif;
  font-weight: 700;
`;

export const Risk = styled.div`
  font-size: 25px;
  color: #21005d;
  font-family: "DM Sans", serif;
  font-weight: 700;
`;

export const ResultItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  margin-left: 80px;
  margin-right: 50px;
`;

export const EmotionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: center;
  font-family: "DM Sans", serif;
  border-radius: 15px;
  padding: 20px;
  margin: 20px 20px 0 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

export const MllmTextStyle = styled.div`
  font-size: 16px;
  font-family: "DM Sans", serif;
`;
